'use client';

import React, { useEffect } from 'react';
import Form from '@dr-pam/common-components/Components/Form/Form';
import FormControl from '@dr-pam/common-components/Components/Form/FormControl';
import TextInput from '@dr-pam/common-components/Components/Form/TextInput';
import Button from '../form/Button';
import { useForm, schemaResolver } from '@mantine/form';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import FetchUtils from '@dr-pam/common-components/Utils/FetchUtils';
import NewsletterSignupAudienceFields from './NewsletterSignupAudienceFields';
import type { NewsletterSignupFormValues, NewsletterSignupPayload } from './newsletterSignupTypes';
import { newsletterSignupValidationSchema } from './newsletterSignupValidation';
import { logNewsletterSignupError, logNewsletterSignupResponse } from './newsletterSignupDebug';

export type NewsletterDetailsModalContentProps = {
	initialEmail: string;
	onCompleted?: () => void;
	onClose?: () => void;
};

export default function NewsletterDetailsModalContent(props: NewsletterDetailsModalContentProps) {
	const { initialEmail, onCompleted, onClose } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const form = useForm<NewsletterSignupFormValues>({
		initialValues: {
			name: '',
			email: initialEmail,
			audienceType: '',
			parentInterest: '',
			professionalType: '',
		},
		validate: schemaResolver(newsletterSignupValidationSchema),
	});

	useEffect(() => {
		form.setFieldValue('email', initialEmail);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialEmail]);

	const handleSubmitDetails = async (values: NewsletterSignupFormValues) => {
		const loader = addLoader();

		try {
			const payload: NewsletterSignupPayload = {
				...values,
				signupSource: 'embedded',
			};

			const request = FetchUtils.postJson('/sign-up/subscribe-to-newsletter', payload);
			const response = await request.response;
			logNewsletterSignupResponse(response);

			NotificationUtils.showSuccess('Successfully subscribed!', 'Mailing list subscription');

			form.reset();
			onCompleted?.();
			onClose?.();
		} catch (err) {
			logNewsletterSignupError(err);
			NotificationUtils.showError(err as Error, 'Error subscribing to the mailing list');
		} finally {
			removeLoader(loader);
		}
	};

	return (
		<Form form={form} onSubmit={handleSubmitDetails} id="newsletter-details-form" className="flex ac c">
			<FormControl name="name" label="Your first name (optional)" disabled={isLoading}>
				<TextInput placeholder="" />
			</FormControl>

			<NewsletterSignupAudienceFields form={form} disabled={isLoading} />

			<Button
				type="submit"
				isLoading={isLoading}
				disabled={isLoading}
				className="dark solid small reverse outline"
			>
				Submit details
			</Button>
		</Form>
	);
}
